// Sources de données — the three ways to add a source (docs/data-sources-v2-spec.md
// §4–5) and their shared form pieces: the path choice, the API in five steps
// (with the configuration assistant beside it, also the « Réglages » of a
// guided source), the web portal shown once on the server's browser, and the
// catalogue in one step. Every API call, URL and payload is the one the page
// always sent (POST /api/integrations/connectors/propose, test-connection,
// evaluate, dry-run, PUT /{key}, catalog/{system}/install; carrier-recipes
// studio sessions for the web path). Secrets come back masked (« ******** »):
// the field stays empty, says « enregistré », and the mask is sent back
// untouched unless a new value is typed (unmask_auth keeps the stored one).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
    ArrowLeft, ArrowRight, Check, ChevronRight, Globe, ListChecks, MousePointerClick, Play, Plug, Plus, Save, Sparkles, Table, Undo2, X,
} from 'lucide-react';
import { Badge, Banner, Card, DescriptionList, EmptyState, Field, IconChip, Switch } from '../design/ui';
import ConnectorAssistant from './ConnectorAssistant';
import { CATEGORIES, categoryTitle } from './dataSources/SourcesByCategory';
import { formatCount } from './dataToolsModel';

export const API = '/api/integrations/connectors';
export const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
export const MASK = '********';
export const SECRET_FIELDS = ['password', 'token', 'value', 'client_secret', 'api_key'];
const AUTH_CHOICES = [
    { type: 'header', fields: ['header_name', 'value'] },
    { type: 'basic', fields: ['username', 'password'] },
    { type: 'bearer', fields: ['token'] },
    { type: 'oauth2', fields: ['token_url', 'client_id', 'client_secret', 'scope'] },
    { type: 'none', fields: [] },
];
const IDENTIFIERS = ['order_number', 'tracking_number', 'customer_email', 'invoice_number', 'client_ref'];
// The fields the guided « champs récupérés » step shows, per section.
export const FIELD_SECTIONS = [
    { section: 'order', list: false, fields: ['number', 'status', 'created_at', 'shipped_at', 'customer_name', 'customer_email', 'client_name', 'shipping_method', 'warehouse', 'total', 'notes'] },
    { section: 'shipments', list: true, fields: ['tracking_number', 'carrier', 'service', 'status', 'shipped_at', 'delivered_at', 'weight'] },
    { section: 'events', list: true, fields: ['date', 'status', 'location', 'code'] },
    { section: 'stock_lines', list: true, fields: ['sku', 'designation', 'barcode', 'quantity_ordered', 'quantity_shipped', 'quantity_available', 'state'] },
    { section: 'recipient_address', list: false, fields: ['firstname', 'lastname', 'company', 'address1', 'address2', 'postcode', 'city', 'country', 'phone', 'email', 'raw'] },
];
const USAGE_MODES = [
    ['enrich_on_ingest', 'dataSources.usage.ingest', 'À la réception de chaque ticket (recommandé)'],
    ['ai_tool', 'dataSources.usage.tool', 'Quand l’agent le demande pendant sa réponse'],
    ['scheduled_pull', 'dataSources.usage.scheduled', 'Toutes les 6 heures pour les tickets ouverts'],
];

export const slug = (text) => String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
const exprText = (expr) => (typeof expr === 'string' ? expr : expr === undefined || expr === null ? '' : JSON.stringify(expr));
const detectKind = (text) => {
    const trimmed = (text || '').trim();
    if (/^curl\b/i.test(trimmed)) return 'curl';
    if (/"openapi"\s*:|"swagger"\s*:|^openapi:|^swagger:|"paths"\s*:|\npaths:/i.test(trimmed)) return 'openapi';
    return 'sample';
};
const emptySpec = () => ({
    spec_version: 1, title: '', transport: 'http_json', inputs: ['order_number'], require_any: ['order_number'], params: {},
    steps: [{ id: 'lookup', request: { method: 'GET', url: '/orders/{order_number}', query: {}, headers: { Accept: 'application/json' }, timeout_s: 30 },
        response: { format: 'json', data_path: '$', not_found_on_404: true }, on_not_found: 'stop', role: 'result' }],
    mapping: {}, modes: ['enrich_on_ingest'], operations: { timeout_s: 30, retries: 1, health_alerts: true }, presentation: { path: 'api', step: 1 },
});
export const newDraftId = () => `draft_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const emptyDraft = (category = null) => ({ key: '', base_url: '', auth: { type: 'header', header_name: 'X-Api-Key', value: '' }, enabled: true, spec: { ...emptySpec(), ...(category ? { category } : {}), presentation: { path: 'api', step: 1, draft_id: newDraftId() } }, sample: null, saved: false });
const failText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : fallback; };

// Apply the dotted patch of an assistant proposal to a draft (whitelist mirrors the backend's).
export const applyAssistantPatch = (draft, patch) => {
    const next = JSON.parse(JSON.stringify(draft));
    Object.entries(patch || {}).forEach(([path, value]) => {
        if (path === 'auth') { next.auth = { ...value }; return; }
        const parts = path.split('.');
        let cursor = next;
        for (let i = 0; i < parts.length - 1; i += 1) {
            const part = parts[i];
            const key = /^\d+$/.test(part) ? Number(part) : part;
            if (cursor[key] === undefined || cursor[key] === null) cursor[key] = /^\d+$/.test(parts[i + 1]) ? [] : {};
            cursor = cursor[key];
        }
        const last = parts[parts.length - 1];
        cursor[/^\d+$/.test(last) ? Number(last) : last] = value;
    });
    return next;
};

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

// A stored secret is never displayed back: the field stays empty and says it
// is saved; typing replaces it; emptying it again keeps the stored one.
export const SecretInput = ({ id, value, onChange, t, autoComplete = 'new-password' }) => {
    const stored = useRef(value === MASK);
    return (
        <input
            id={id}
            className="u-input"
            type="password"
            autoComplete={autoComplete}
            value={value === MASK ? '' : (value || '')}
            placeholder={stored.current ? t('dataToolsUi.secret.stored', 'Enregistré — saisissez pour remplacer') : '••••••••'}
            onChange={(e) => onChange(e.target.value || (stored.current ? MASK : ''))}
        />
    );
};

export const secretHelp = (value, t) => (value === MASK
    ? t('dataToolsUi.secret.storedHelp', 'Une valeur est enregistrée. Elle n’est jamais réaffichée.')
    : t('dataToolsUi.secret.help', 'Enregistré pour les lectures, jamais réaffiché.'));

// « Type de système » select of the creation steps (spec.category).
const CategorySelect = ({ t, value, onChange, id }) => (
    <Field label={t('dataSources.step1.category', 'Type de système')} htmlFor={id}>
        <select id={id} className="u-select" value={value || ''} onChange={(e) => onChange(e.target.value || null)}>
            <option value="">{t('dataSources.step1.categoryAuto', 'Déduit des champs lus')}</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{categoryTitle(c.key, t)}</option>)}
        </select>
    </Field>
);

// Horizontal steps of a creation path. Reachable steps are buttons.
const Stepper = ({ names, labelOf, step, canGo, onGo, label }) => (
    <ol className="ds-stepper" aria-label={label}>
        {names.map((name, i) => {
            const n = i + 1;
            const state = step === n ? 'is-current' : step > n ? 'is-done' : '';
            const reachable = canGo ? canGo(n) : false;
            const content = (
                <>
                    <span className="ds-stepper__marker" aria-hidden="true">{step > n ? <Check strokeWidth={2.4} /> : n}</span>
                    <span className="ds-stepper__label">{labelOf(name)}</span>
                </>
            );
            return (
                <li key={name} className={`ds-stepper__item ${state}`} aria-current={step === n ? 'step' : undefined}>
                    {reachable && step !== n
                        ? <button type="button" className="ds-stepper__btn" onClick={() => onGo(n)}>{content}</button>
                        : <span className="ds-stepper__btn">{content}</span>}
                </li>
            );
        })}
    </ol>
);

// Toggle chips (a few values, several allowed): identifiers, modes.
export const ChipToggle = ({ checked, onChange, children, title }) => (
    <label className={`ds-chip${checked ? ' is-on' : ''}`} title={title}>
        <input type="checkbox" checked={checked} onChange={onChange} />
        {checked && <Check strokeWidth={2.4} aria-hidden="true" />}
        {children}
    </label>
);

const Message = ({ message }) => (message
    ? <Banner tone={message.type === 'ok' ? 'success' : 'danger'}>{message.text}</Banner>
    : null);

const IdentifierInputs = ({ t, names, values, onChange, idPrefix }) => names.map((name) => (
    <input key={name} id={`${idPrefix}-${name}`} className="u-input ds-ident" aria-label={t(`ticketWorkflow.identifier.${name}`, name)}
        placeholder={t(`ticketWorkflow.identifier.${name}`, name)} value={values[name] || ''}
        onChange={(e) => onChange({ ...values, [name]: e.target.value })} />
));

const FieldRow = ({ t, label, expr, value, selected, onSelect, onClear }) => (
    <div className={`ds-fieldrow${selected ? ' is-selected' : ''}${expr ? ' is-mapped' : ''}`}>
        <button type="button" className="ds-fieldrow__main" onClick={onSelect} aria-pressed={selected}>
            <span className="ds-fieldrow__label">{label}</span>
            <span className="ds-fieldrow__value">
                {value !== undefined && value !== null
                    ? String(value)
                    : <em>{expr ? t('dataSources.step4.noValue', 'aucune valeur dans l’exemple') : t('dataSources.step4.unmapped', 'non affecté')}</em>}
            </span>
            {expr && <code className="u-code ds-fieldrow__expr">{exprText(expr)}</code>}
        </button>
        {onClear && (
            <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={onClear}
                aria-label={t('dataToolsUi.create.clearField', 'Retirer l’affectation')} title={t('dataToolsUi.create.clearField', 'Retirer l’affectation')}>
                <X aria-hidden="true" />
            </button>
        )}
    </div>
);

const ParamsEditor = ({ t, label, value, onChange }) => {
    const entries = Object.entries(value || {});
    const set = (index, k, v) => { const next = entries.map(([ek, ev], i) => (i === index ? [k, v] : [ek, ev])); onChange(Object.fromEntries(next.filter(([ek]) => ek))); };
    return (
        <div className="ds-params">
            <span className="u-label">{label}</span>
            {entries.map(([k, v], i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="ds-params__row">
                    <input className="u-input ds-mono ds-params__key" value={k} onChange={(e) => set(i, e.target.value, v)} placeholder={t('dataSources.step3.key', 'clé')}
                        aria-label={t('dataSources.step3.key', 'clé')} />
                    <input className="u-input ds-mono" value={typeof v === 'string' ? v : JSON.stringify(v)} onChange={(e) => set(i, k, e.target.value)} placeholder="{order_number}"
                        aria-label={t('dataToolsUi.create.paramValue', 'Valeur')} />
                    <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => onChange(Object.fromEntries(entries.filter((_, j) => j !== i)))}
                        aria-label={t('dataToolsUi.create.removeParam', 'Retirer ce paramètre')} title={t('dataToolsUi.create.removeParam', 'Retirer ce paramètre')}>
                        <X aria-hidden="true" />
                    </button>
                </div>
            ))}
            <button type="button" className="u-btn u-btn--ghost u-btn--sm ds-params__add" onClick={() => onChange({ ...(value || {}), '': '' })}>
                <Plus aria-hidden="true" /> {t('dataSources.step3.addParam', 'Ajouter')}
            </button>
        </div>
    );
};

const UsageSwitches = ({ t, modes, onToggle, idPrefix }) => (
    <div className="ds-switches">
        {USAGE_MODES.map(([mode, key, label]) => (
            <Switch key={mode} id={`${idPrefix}-${mode}`} checked={modes.includes(mode)} onChange={(on) => onToggle(mode, on)} label={t(key, label)} />
        ))}
    </div>
);

// ---------------------------------------------------------------------------
// Path choice
// ---------------------------------------------------------------------------

export const ChoosePath = ({ t, onApi, onWeb, onCatalog }) => {
    const choices = [
        { key: 'api', icon: Plug, title: t('dataSources.choose.api', 'Il a une API'), desc: t('dataSources.choose.apiDesc', 'Une adresse, une clé ou un identifiant, et la documentation ou un exemple de réponse.'), onClick: onApi },
        { key: 'web', icon: Globe, title: t('dataSources.choose.web', 'Il a un site web sur lequel je me connecte'), desc: t('dataSources.choose.webDesc', 'Vous montrez une fois comment vous trouvez une commande ; le système la relit ensuite à chaque ticket.'), onClick: onWeb },
        { key: 'catalog', icon: ListChecks, title: t('dataSources.choose.catalog', 'Il est dans la liste'), desc: t('dataSources.choose.catalogDesc', 'EffiTrace, Spacefill, Ship24, les API de suivi de vos marques : une seule étape.'), onClick: onCatalog },
    ];
    return (
        <Card title={t('dataSources.choose.title', 'Comment accède-t-on à ce système ?')}
            description={t('dataToolsUi.create.chooseDesc', 'Choisissez comment Unanimity lit les commandes de ce système. Rien n’est modifié chez lui : la source lit seulement.')}>
            <div className="ds-choices">
                {choices.map(({ key, icon, title, desc, onClick }) => (
                    <button key={key} type="button" className="ds-choice" onClick={onClick}>
                        <IconChip icon={icon} />
                        <span className="ds-choice__text">
                            <span className="ds-choice__title">{title}</span>
                            <span className="ds-choice__desc">{desc}</span>
                        </span>
                        <ChevronRight className="ds-choice__chevron" aria-hidden="true" />
                    </button>
                ))}
            </div>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Path A — API, five steps
// ---------------------------------------------------------------------------

const STEPS = ['name', 'connection', 'search', 'fields', 'verify'];
const STEP_LABELS = { name: 'Nom', connection: 'Connexion', search: 'Recherche', fields: 'Champs', verify: 'Vérification' };

export const ApiWizard = ({ t, onBack, onDone, initial = null, onSaved = null, category = null }) => {
    const [draft, setDraft] = useState(initial || emptyDraft(category));
    const [step, setStep] = useState(initial?.spec?.presentation?.step && initial.saved ? 5 : 1);
    const [busy, setBusy] = useState(null);
    const [message, setMessage] = useState(null);
    const [docText, setDocText] = useState('');
    const [connection, setConnection] = useState(null);
    const [evaluation, setEvaluation] = useState(null);
    const [selectedField, setSelectedField] = useState(null);
    const [verification, setVerification] = useState(null);
    const [identifiers, setIdentifiers] = useState({});
    const [lastTest, setLastTest] = useState(null);

    const spec = draft.spec;
    const draftId = draft.saved ? draft.key : (spec.presentation?.draft_id || null);
    const applyFromAssistant = (patch, sample) => {
        setDraft((d) => {
            const next = applyAssistantPatch(d, patch);
            if (sample !== null && sample !== undefined) next.sample = sample;
            return next;
        });
        setMessage({ type: 'ok', text: t('dataSources.assistantApplied', 'Proposition appliquée : vérifiez les étapes concernées.') });
    };
    const request = spec.steps[0].request;
    const response = spec.steps[0].response;
    const setSpec = (patch) => setDraft((d) => ({ ...d, spec: { ...d.spec, ...patch } }));
    const setRequest = (patch) => setSpec({ steps: [{ ...spec.steps[0], request: { ...request, ...patch } }] });
    const setResponse = (patch) => setSpec({ steps: [{ ...spec.steps[0], response: { ...response, ...patch } }] });
    const fail = (e, fallback) => setMessage({ type: 'error', text: failText(e, fallback) });

    const analyse = async () => {
        if (!docText.trim()) return;
        setBusy('analyse');
        setMessage(null);
        try {
            const kind = detectKind(docText);
            const payload = kind === 'sample' ? { sample: docText, base_url: draft.base_url, request, title: spec.title } : { text: docText, title: spec.title };
            const res = await axios.post(`${API}/propose`, { kind, payload, use_model: true }, authHeaders());
            const proposal = res.data;
            if (kind === 'sample') {
                setDraft((d) => ({ ...d, sample: proposal.sample, spec: { ...d.spec, mapping: proposal.spec.mapping, steps: [{ ...d.spec.steps[0], response: { ...d.spec.steps[0].response, data_path: proposal.spec.steps[0].response.data_path } }] } }));
            } else {
                setDraft((d) => ({
                    ...d, base_url: proposal.base_url || d.base_url, auth: proposal.auth?.type ? proposal.auth : d.auth, sample: proposal.sample || d.sample,
                    spec: { ...d.spec, title: d.spec.title || proposal.spec.title, inputs: proposal.spec.inputs?.length ? proposal.spec.inputs : d.spec.inputs,
                        require_any: proposal.spec.require_any?.length ? proposal.spec.require_any : d.spec.require_any, steps: proposal.spec.steps, mapping: proposal.spec.mapping || d.spec.mapping,
                        tool: proposal.spec.tool || d.spec.tool },
                }));
            }
            setMessage({ type: 'ok', text: [t('dataSources.analysed', 'Documentation lue : les étapes suivantes sont pré-remplies.'), ...(proposal.warnings || [])].join(' ') });
        } catch (e) {
            fail(e, t('dataSources.analyseError', 'La documentation n’a pas pu être lue.'));
        } finally {
            setBusy(null);
        }
    };

    const testConnection = async () => {
        setBusy('connection');
        setConnection(null);
        try {
            const res = await axios.post(`${API}/test-connection`, { key: draft.saved ? draft.key : null, base_url: draft.base_url, auth: draft.auth, spec }, authHeaders());
            setConnection(res.data);
            setLastTest({ ...res.data, stamp: Date.now(), identifiers: {} });
        } catch (e) {
            fail(e, t('dataSources.testError', 'Le test a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const evaluate = useCallback(async (mapping, sample) => {
        if (sample === null || sample === undefined) { setEvaluation(null); return; }
        try {
            const res = await axios.post(`${API}/evaluate`, { mapping, sample, data_path: response.data_path }, authHeaders());
            setEvaluation(res.data);
        } catch (e) { setEvaluation(null); }
    }, [response.data_path]);

    useEffect(() => { if (step === 4) evaluate(spec.mapping, draft.sample); }, [step, spec.mapping, draft.sample, evaluate]);

    const readRealOrder = async () => {
        setBusy('read');
        setMessage(null);
        try {
            const res = await axios.post(`${API}/dry-run`, { key: draft.saved ? draft.key : null, spec, base_url: draft.base_url, auth: draft.auth, identifiers }, authHeaders());
            if (res.data.raw && typeof res.data.raw === 'object' && !res.data.raw._truncated) {
                // the raw data comes after data_path: keep the mapping relative to it
                setDraft((d) => ({ ...d, sample: res.data.raw, spec: { ...d.spec, steps: [{ ...d.spec.steps[0], response: { ...d.spec.steps[0].response, data_path: '$' } }] } }));
                setMessage({ type: 'ok', text: t('dataSources.readOk', 'Réponse reçue : affectez les champs ci-dessous.') });
            } else {
                setMessage({ type: 'error', text: `${res.data.reason}${res.data.error ? ` — ${res.data.error}` : ''}` });
                setLastTest({ ok: false, found: false, reason: res.data.reason, error: res.data.error, raw: res.data.raw, steps: res.data.steps, identifiers, stamp: Date.now() });
            }
        } catch (e) {
            fail(e, t('dataSources.readError', 'La lecture a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const proposeFromSample = async () => {
        if (!draft.sample) return;
        setBusy('propose');
        try {
            const res = await axios.post(`${API}/propose`, { kind: 'sample', payload: { sample: draft.sample, base_url: draft.base_url, request, title: spec.title }, use_model: true }, authHeaders());
            setSpec({ mapping: res.data.spec.mapping });
            setResponse({ data_path: res.data.spec.steps[0].response.data_path });
            setMessage({ type: 'ok', text: t('dataSources.proposed', 'Champs proposés : vérifiez-les et corrigez d’un clic.') });
        } catch (e) {
            fail(e, t('dataSources.analyseError', 'La documentation n’a pas pu être lue.'));
        } finally {
            setBusy(null);
        }
    };

    const assign = (row) => {
        if (!selectedField) return;
        const { section, field } = selectedField;
        const meta = FIELD_SECTIONS.find((s) => s.section === section);
        const mapping = JSON.parse(JSON.stringify(spec.mapping || {}));
        if (meta.list) {
            if (field === '__each') {
                mapping[section] = { each: row.path, fields: mapping[section]?.fields || {} };
            } else {
                const each = mapping[section]?.each;
                let relative = row.path;
                if (each && row.path.startsWith(each)) relative = '$' + row.path.slice(each.length).replace(/^\[\d+\]/, '');
                mapping[section] = { each: each || '', fields: { ...(mapping[section]?.fields || {}), [field]: relative } };
            }
        } else {
            mapping[section] = { ...(mapping[section] || {}), [field]: row.path };
        }
        setSpec({ mapping });
    };

    const clearField = (section, field) => {
        const mapping = JSON.parse(JSON.stringify(spec.mapping || {}));
        const meta = FIELD_SECTIONS.find((s) => s.section === section);
        if (meta.list) { if (mapping[section]?.fields) delete mapping[section].fields[field]; } else if (mapping[section]) delete mapping[section][field];
        setSpec({ mapping });
    };

    const verify = async () => {
        setBusy('verify');
        setVerification(null);
        try {
            const res = await axios.post(`${API}/dry-run`, { key: draft.saved ? draft.key : null, spec, base_url: draft.base_url, auth: draft.auth, identifiers }, authHeaders());
            setVerification(res.data);
            setLastTest({ ok: Boolean(res.data.found), found: res.data.found, reason: res.data.reason, error: res.data.error, raw: res.data.raw, steps: res.data.steps, identifiers, stamp: Date.now() });
        } catch (e) {
            fail(e, t('dataSources.readError', 'La lecture a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const save = async () => {
        const key = draft.key || slug(spec.title);
        if (!key) { setMessage({ type: 'error', text: t('dataSources.nameRequired', 'Donnez un nom à la source.') }); return; }
        setBusy('save');
        setMessage(null);
        try {
            const toSave = { ...spec, presentation: { ...(spec.presentation || {}), path: 'api', step }, tool: spec.modes.includes('ai_tool') ? (spec.tool?.name ? spec.tool : { name: `lookup_${key}`, description: `Look up an order in ${spec.title || key}` }) : undefined };
            const res = await axios.put(`${API}/${encodeURIComponent(key)}`, { spec: toSave, base_url: draft.base_url, auth: draft.auth, enabled: draft.enabled }, authHeaders());
            setDraft((d) => ({ ...d, key, saved: true, auth: res.data.auth, spec: res.data.spec }));
            if (onSaved) onSaved(res.data); else onDone(key);
        } catch (e) {
            fail(e, t('dataSources.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const canNext = { 1: Boolean(spec.title), 2: Boolean(draft.base_url), 3: spec.inputs.length > 0 && Boolean(request.url), 4: true, 5: true }[step];
    const authChoice = AUTH_CHOICES.find((c) => c.type === draft.auth?.type) || AUTH_CHOICES[0];
    const stepLabel = (name) => t(`dataSources.steps.${name}`, STEP_LABELS[name] || name);
    const toggleMode = (mode, on) => setSpec({ modes: on ? [...spec.modes, mode] : spec.modes.filter((m) => m !== mode), schedule: mode === 'scheduled_pull' && on ? { every_minutes: 360 } : spec.schedule });

    const stepBody = () => {
        if (step === 1) {
            return (
                <div className="u-form">
                    <Field label={t('dataSources.step1.name', 'Nom de la source')} htmlFor="ds-api-name">
                        <input id="ds-api-name" className="u-input" value={spec.title} placeholder={t('dataSources.step1.namePlaceholder', 'ex. WMS Acme')}
                            onChange={(e) => { setSpec({ title: e.target.value }); if (!draft.saved) setDraft((d) => ({ ...d, key: slug(e.target.value) })); }} />
                    </Field>
                    <CategorySelect t={t} id="ds-api-category" value={spec.category} onChange={(value) => setSpec({ category: value || undefined })} />
                    <Field label={t('dataSources.step1.docs', 'Documentation (facultatif) : collez un fichier OpenAPI, une commande cURL ou une réponse d’exemple')} htmlFor="ds-api-docs"
                        help={t('dataSources.step1.docsPlaceholder', 'Le système lit ce que vous collez et pré-remplit la connexion, la recherche et les champs.')}>
                        <textarea id="ds-api-docs" className="u-textarea ds-mono ds-code-input" rows={7} value={docText} spellCheck={false} onChange={(e) => setDocText(e.target.value)} />
                    </Field>
                    <div>
                        <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'analyse' || !docText.trim()} onClick={analyse}>
                            <Sparkles aria-hidden="true" /> {busy === 'analyse' ? t('dataToolsUi.create.reading', 'Lecture…') : t('dataSources.step1.analyse', 'Lire la documentation')}
                        </button>
                    </div>
                </div>
            );
        }
        if (step === 2) {
            return (
                <div className="u-form">
                    <Field label={t('dataSources.step2.baseUrl', 'Adresse de l’API')} htmlFor="ds-api-base">
                        <input id="ds-api-base" className="u-input ds-mono" value={draft.base_url} placeholder="https://wms.example.com/api" onChange={(e) => setDraft((d) => ({ ...d, base_url: e.target.value }))} />
                    </Field>
                    <Field label={t('dataSources.step2.authType', 'Type d’accès')} htmlFor="ds-api-auth">
                        <select id="ds-api-auth" className="u-select" value={draft.auth?.type || 'none'}
                            onChange={(e) => setDraft((d) => ({ ...d, auth: { type: e.target.value, ...(e.target.value === 'header' ? { header_name: 'X-Api-Key' } : {}) } }))}>
                            {AUTH_CHOICES.map((c) => <option key={c.type} value={c.type}>{t(`dataSources.auth.${c.type}`, c.type)}</option>)}
                        </select>
                    </Field>
                    {authChoice.fields.length > 0 && (
                        <div className="u-form-grid">
                            {authChoice.fields.map((field) => (
                                <Field key={field} label={t(`dataSources.authField.${field}`, field)} htmlFor={`ds-api-auth-${field}`}
                                    help={SECRET_FIELDS.includes(field) ? secretHelp(draft.auth?.[field], t) : undefined}>
                                    {SECRET_FIELDS.includes(field)
                                        ? <SecretInput id={`ds-api-auth-${field}`} t={t} value={draft.auth?.[field]} onChange={(v) => setDraft((d) => ({ ...d, auth: { ...d.auth, [field]: v } }))} />
                                        : <input id={`ds-api-auth-${field}`} className="u-input" value={draft.auth?.[field] || ''} onChange={(e) => setDraft((d) => ({ ...d, auth: { ...d.auth, [field]: e.target.value } }))} />}
                                </Field>
                            ))}
                        </div>
                    )}
                    <div>
                        <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'connection' || !draft.base_url} onClick={testConnection}>
                            <Play aria-hidden="true" /> {busy === 'connection' ? t('dataToolsUi.create.testing', 'Test…') : t('dataSources.step2.test', 'Tester la connexion')}
                        </button>
                    </div>
                    {connection && (
                        <Banner tone={connection.ok ? 'success' : 'danger'}>
                            {connection.explanation}{connection.error && !connection.ok ? ` (${connection.error})` : ''}
                        </Banner>
                    )}
                </div>
            );
        }
        if (step === 3) {
            return (
                <div className="u-form">
                    <div className="u-field">
                        <span className="u-label">{t('dataSources.step3.with', 'Avec quoi cherchez-vous une commande ?')}</span>
                        <div className="ds-chips">
                            {IDENTIFIERS.map((name) => (
                                <ChipToggle key={name} checked={spec.inputs.includes(name)}
                                    onChange={() => { const inputs = spec.inputs.includes(name) ? spec.inputs.filter((v) => v !== name) : [...spec.inputs, name]; setSpec({ inputs, require_any: inputs }); }}>
                                    {t(`ticketWorkflow.identifier.${name}`, name)}
                                </ChipToggle>
                            ))}
                        </div>
                    </div>
                    <div className="ds-inline">
                        <Field label={t('dataSources.step3.method', 'Méthode')} htmlFor="ds-api-method">
                            <select id="ds-api-method" className="u-select ds-short" value={request.method} onChange={(e) => setRequest({ method: e.target.value })}>
                                {['GET', 'POST'].map((m) => <option key={m}>{m}</option>)}
                            </select>
                        </Field>
                        <div className="ds-grow">
                            <Field label={t('dataSources.step3.path', 'Chemin de la recherche')} htmlFor="ds-api-path"
                                help={t('dataSources.step3.hint', 'Écrivez un identifiant entre accolades là où le système l’attend : {order_number}, {tracking_number}, {customer_email}…')}>
                                <input id="ds-api-path" className="u-input ds-mono" value={request.url} placeholder="/orders/{order_number}" onChange={(e) => setRequest({ url: e.target.value })} />
                            </Field>
                        </div>
                    </div>
                    <ParamsEditor t={t} label={t('dataSources.step3.query', 'Paramètres de l’adresse (?clé=valeur)')} value={request.query || {}} onChange={(query) => setRequest({ query })} />
                    {request.method === 'POST' && <ParamsEditor t={t} label={t('dataSources.step3.body', 'Champs envoyés (JSON)')} value={request.body || {}} onChange={(body) => setRequest({ body })} />}
                    <Field label={t('dataSources.step3.dataPath', 'Où se trouve la commande dans la réponse ? (laissez $ si c’est toute la réponse)')} htmlFor="ds-api-datapath">
                        <input id="ds-api-datapath" className="u-input ds-mono ds-short" value={response.data_path || '$'} onChange={(e) => setResponse({ data_path: e.target.value })} />
                    </Field>
                </div>
            );
        }
        if (step === 4) {
            return (
                <div className="u-form">
                    <p className="u-help ds-help">{t('dataSources.step4.hint', 'Collez un exemple de réponse, ou lisez une vraie commande. Puis sélectionnez un champ à droite et cliquez sa valeur à gauche.')}</p>
                    <div className="ds-toolbar">
                        <IdentifierInputs t={t} names={spec.inputs} values={identifiers} onChange={setIdentifiers} idPrefix="ds-api-read" />
                        <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'read' || !draft.base_url} onClick={readRealOrder}>
                            <Play aria-hidden="true" /> {t('dataSources.step4.read', 'Lire une vraie commande')}
                        </button>
                        <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'propose' || !draft.sample} onClick={proposeFromSample}>
                            <Sparkles aria-hidden="true" /> {t('dataSources.step4.propose', 'Proposer automatiquement')}
                        </button>
                    </div>
                    <div className="ds-split">
                        <div className="ds-pane">
                            <span className="u-overline">{t('dataToolsUi.create.answer', 'Réponse du système')}</span>
                            <textarea className="u-textarea ds-mono ds-sample" spellCheck={false} aria-label={t('dataSources.step4.samplePlaceholder', 'Collez ici un exemple de réponse (JSON)')}
                                placeholder={t('dataSources.step4.samplePlaceholder', 'Collez ici un exemple de réponse (JSON)')}
                                value={typeof draft.sample === 'string' ? draft.sample : draft.sample ? JSON.stringify(draft.sample, null, 2) : ''}
                                onChange={(e) => { const text = e.target.value; try { setDraft((d) => ({ ...d, sample: JSON.parse(text) })); } catch (err) { setDraft((d) => ({ ...d, sample: text })); } }} />
                            {evaluation?.rows && (
                                <div className="ds-values" role="list">
                                    {!selectedField && <p className="u-help">{t('dataToolsUi.create.pickFieldFirst', 'Choisissez d’abord un champ à droite.')}</p>}
                                    {evaluation.rows.map((row) => (
                                        <button type="button" role="listitem" key={row.path} className={`ds-value ds-value--${row.kind}`} disabled={!selectedField} onClick={() => assign(row)} title={row.path}>
                                            <code>{row.path.replace(/^\$\.?/, '')}</code>
                                            <span>{String(row.value ?? '∅')}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="ds-pane">
                            <span className="u-overline">{t('dataToolsUi.create.fieldsRead', 'Champs lus par Unanimity')}</span>
                            <div className="ds-sections">
                                {FIELD_SECTIONS.map((meta) => (
                                    <details key={meta.section} open={meta.section === 'order' || meta.section === 'shipments'} className="u-disclosure ds-section">
                                        <summary><ChevronRight aria-hidden="true" />{t(`connectorStudio.section_${meta.section}`, meta.section)}</summary>
                                        <div className="ds-section__body">
                                            {meta.list && (
                                                <FieldRow t={t} label={t('dataSources.step4.each', 'Liste')} expr={spec.mapping?.[meta.section]?.each}
                                                    value={evaluation?.canonical?.[meta.section]?.length !== undefined ? `${evaluation.canonical[meta.section].length} ${t('dataSources.step4.items', 'éléments')}` : null}
                                                    selected={selectedField?.section === meta.section && selectedField.field === '__each'} onSelect={() => setSelectedField({ section: meta.section, field: '__each' })} onClear={null} />
                                            )}
                                            {meta.fields.map((field) => {
                                                const expr = meta.list ? spec.mapping?.[meta.section]?.fields?.[field] : spec.mapping?.[meta.section]?.[field];
                                                const value = meta.list ? evaluation?.canonical?.[meta.section]?.[0]?.[field] : evaluation?.canonical?.[meta.section]?.[field];
                                                return (
                                                    <FieldRow key={field} t={t} label={t(`dataSources.field.${meta.section}.${field}`, field)} expr={expr} value={value}
                                                        selected={selectedField?.section === meta.section && selectedField.field === field}
                                                        onSelect={() => setSelectedField({ section: meta.section, field })} onClear={expr ? () => clearField(meta.section, field) : null} />
                                                );
                                            })}
                                        </div>
                                    </details>
                                ))}
                            </div>
                            {evaluation?.errors?.length > 0 && <Banner tone="danger">{evaluation.errors.join(' · ')}</Banner>}
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="u-form">
                <div className="u-field">
                    <span className="u-label">{t('dataSources.step5.check', 'Vérifier avec une commande')}</span>
                    <div className="ds-toolbar">
                        <IdentifierInputs t={t} names={spec.inputs} values={identifiers} onChange={setIdentifiers} idPrefix="ds-api-verify" />
                        <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'verify'} onClick={verify}>
                            <Play aria-hidden="true" /> {t('dataSources.step5.verify', 'Vérifier avec cette commande')}
                        </button>
                    </div>
                </div>
                {verification && (
                    <Banner tone={verification.found ? 'success' : 'warning'}>
                        {verification.found
                            ? t('dataSources.step5.found', 'Commande trouvée en {{ms}} ms.', { ms: verification.duration_ms })
                            : `${verification.reason}${verification.error ? ` — ${verification.error}` : ''}`}
                    </Banner>
                )}
                {verification?.prompt_preview && (
                    <div className="u-field">
                        <span className="u-label">{t('dataSources.step5.preview', 'Ce que l’agent lira')}</span>
                        <pre className="ds-pre">{verification.prompt_preview}</pre>
                    </div>
                )}
                <div className="u-field">
                    <span className="u-label">{t('dataSources.step5.usage', 'Quand l’utiliser')}</span>
                    <UsageSwitches t={t} modes={spec.modes} onToggle={toggleMode} idPrefix="ds-api-usage" />
                    <p className="u-help">{t('dataSources.step5.workflowHint', 'L’ordre entre les sources, les conditions par canal ou par client et le comportement quand rien n’est trouvé se règlent dans « Parcours d’un ticket ».')}</p>
                </div>
            </div>
        );
    };

    return (
        <div className="ds-with-assistant">
            <Card
                className="ds-wizard"
                title={`${step}. ${stepLabel(STEPS[step - 1])}`}
                description={draft.saved
                    ? t('dataToolsUi.create.editDesc', 'Revenez sur n’importe quelle étape ; rien ne change avant « Enregistrer ».')
                    : t('dataToolsUi.create.apiDesc', 'Cinq étapes : la source est enregistrée et active à la dernière.')}
                footerNote={draft.saved ? null : t('dataToolsUi.create.stepOf', 'Étape {{step}} sur {{total}}', { step, total: STEPS.length })}
                footer={(
                    <div className="ds-wizard__nav">
                        {onBack && !draft.saved && step === 1 && (
                            <button type="button" className="u-btn u-btn--secondary" onClick={onBack}><ArrowLeft aria-hidden="true" /> {t('dataSources.back', 'Retour')}</button>
                        )}
                        {step > 1 && (
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setStep(step - 1)}>
                                <ArrowLeft aria-hidden="true" /> {t('dataSources.prev', 'Précédent')}
                            </button>
                        )}
                        {step < 5 && (
                            <button type="button" className="u-btn u-btn--primary" disabled={!canNext} onClick={() => setStep(step + 1)}>
                                {t('dataSources.next', 'Suivant')} <ArrowRight aria-hidden="true" />
                            </button>
                        )}
                        {step === 5 && (
                            <button type="button" className="u-btn u-btn--primary" disabled={busy === 'save'} onClick={save}>
                                <Save aria-hidden="true" /> {draft.saved ? t('dataSources.saveChanges', 'Enregistrer les modifications') : t('dataSources.activate', 'Enregistrer et activer')}
                            </button>
                        )}
                    </div>
                )}
            >
                <Stepper names={STEPS} labelOf={stepLabel} step={step} label={t('dataToolsUi.create.steps', 'Étapes')}
                    canGo={(n) => draft.saved || step > n} onGo={setStep} />
                <div className="ds-wizard__body">
                    <Message message={message} />
                    {stepBody()}
                </div>
            </Card>
            <ConnectorAssistant draftId={draftId} draft={{ key: draft.key, base_url: draft.base_url, auth: draft.auth, spec }} lastTest={lastTest} onApply={applyFromAssistant}
                stepHint={stepLabel(STEPS[step - 1])} />
        </div>
    );
};

// ---------------------------------------------------------------------------
// Path B — web portal: show once how an order is found, the system re-reads it
// ---------------------------------------------------------------------------

const RECIPES = '/api/logistics/carrier-recipes';
const WEB_STEPS = ['name', 'connection', 'search', 'read', 'verify'];
const WEB_LABELS = { name: 'Nom', connection: 'Connexion', search: 'Trouver', read: 'Lire', verify: 'Vérification' };
const SEARCH_VARIABLES = ['order_number', 'tracking_number', 'customer_email', 'invoice_number', 'client_ref', 'client_name'];
const LIST_SECTIONS = ['shipments', 'events', 'stock_lines'];

export const WebWizard = ({ t, catalog, onBack, onDone, category: initialCategory = null }) => {
    const [step, setStep] = useState(1);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState(initialCategory);
    const [startUrl, setStartUrl] = useState('');
    const [session, setSession] = useState(null);
    const [frame, setFrame] = useState(null);
    const [frameKey, setFrameKey] = useState(0);
    const [frameSrc, setFrameSrc] = useState(null);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState('');
    const [value, setValue] = useState('');
    const [variable, setVariable] = useState('username');
    const [secret, setSecret] = useState(false);
    const [clientBound, setClientBound] = useState(false);
    const [field, setField] = useState({ section: 'order', name: 'status' });
    const [tablePick, setTablePick] = useState(null); // { t, section, columns: {index: field} }
    const [hints, setHints] = useState(null);
    const [recipe, setRecipe] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [identifiers, setIdentifiers] = useState({});
    const [account, setAccount] = useState({ username: '', password: '' });
    const [verifyDays, setVerifyDays] = useState(true);
    const [modes, setModes] = useState(['enrich_on_ingest']);
    const [busy, setBusy] = useState(null);
    const [message, setMessage] = useState(null);
    const sessionRef = useRef(null);
    const draftId = useRef(newDraftId());
    const fail = (e, fallback) => setMessage({ type: 'error', text: failText(e, fallback) });
    const key = slug(title);
    const effitrace = catalog.find((e) => e.system === 'effitrace' && e.verification_available);

    useEffect(() => () => { if (sessionRef.current) axios.delete(`${RECIPES}/studio/sessions/${sessionRef.current}`, authHeaders()).catch(() => {}); }, []);

    // The frame is behind the same bearer token as the rest of the API: fetch it as a blob.
    useEffect(() => {
        if (!session || !frameKey) return undefined;
        let url = null;
        let cancelled = false;
        axios.get(`${RECIPES}/studio/sessions/${session.id}/screenshot`, { ...authHeaders(), responseType: 'blob' })
            .then((res) => { if (cancelled) return; url = URL.createObjectURL(res.data); setFrameSrc(url); })
            .catch(() => { if (!cancelled) setFrameSrc(null); });
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [session, frameKey]);

    const showFrame = (data) => { setFrame(data.frame || data); setFrameKey((k) => k + 1); setSelected(null); setValue(''); setHints(null); };

    const openSession = async () => {
        setBusy('open');
        setMessage(null);
        try {
            const res = await axios.post(`${RECIPES}/studio/sessions`, { carrier: title || key, operation: 'lookup', start_url: startUrl }, authHeaders());
            setSession(res.data.session);
            sessionRef.current = res.data.session.id;
            showFrame(res.data);
        } catch (e) {
            fail(e, t('dataSources.web.openError', 'Impossible d’ouvrir le portail sur le serveur.'));
        } finally {
            setBusy(null);
        }
    };

    const act = async (action, extra = {}) => {
        if (!session) return null;
        setBusy('act');
        setMessage(null);
        try {
            const res = await axios.post(`${RECIPES}/studio/sessions/${session.id}/act`, { action, k: selected?.k ?? null, value: value || null, ...extra }, authHeaders());
            showFrame(res.data);
            return res.data;
        } catch (e) {
            fail(e, t('dataSources.web.actError', 'L’action a échoué sur le portail.'));
            return null;
        } finally {
            setBusy(null);
        }
    };
    const undo = async () => {
        if (!session) return;
        setBusy('act');
        try {
            const res = await axios.post(`${RECIPES}/studio/sessions/${session.id}/undo`, {}, authHeaders());
            showFrame(res.data);
        } catch (e) {
            fail(e, t('dataSources.web.actError', 'L’action a échoué sur le portail.'));
        } finally {
            setBusy(null);
        }
    };

    const fillVariable = () => act('fill', { variable: step === 2 ? variable : (clientBound ? 'client_name' : variable), secret: step === 2 && secret });
    const selectVariable = () => act('select', { variable: clientBound ? 'client_name' : null });
    const extractField = async () => {
        const name = `${field.section}_${field.name}`;
        const res = await act('extract', { variable: name });
        if (res?.value !== undefined) setMessage({ type: 'ok', text: t('dataSources.web.extracted', '« {{value}} » lu comme {{field}}.', { value: res.value, field: t(`dataSources.field.${field.section}.${field.name}`, field.name) }) });
    };
    const extractTable = async () => {
        if (!tablePick) return;
        const columns = Object.fromEntries(Object.entries(tablePick.columns).filter(([, v]) => v));
        setSelected(null);
        const res = await act('extract_table', { k: tablePick.t, variable: tablePick.section, fields: columns });
        if (res) { setMessage({ type: 'ok', text: t('dataSources.web.tableExtracted', 'Tableau lu comme {{section}} ({{n}} colonnes).', { section: t(`connectorStudio.section_${tablePick.section}`, tablePick.section), n: Object.keys(columns).length }) }); setTablePick(null); }
    };
    const readPageHints = async () => {
        if (!frame?.text) return;
        setBusy('hints');
        try {
            const res = await axios.post(`${API}/assistant/read-page`, { text: frame.text }, authHeaders());
            setHints(res.data);
        } catch (e) {
            fail(e, t('assistant.explainError', 'L’explication a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const finish = async () => {
        if (!session) return;
        setBusy('finish');
        setMessage(null);
        try {
            const drafted = await axios.post(`${RECIPES}/studio/sessions/${session.id}/draft?review=false`, {}, authHeaders());
            const draft = { ...drafted.data.draft, description: `${title} — ${t('dataSources.web.recipeDescription', 'lecture de commande enregistrée dans le parcours site web')}` };
            const saved = await axios.post(`${RECIPES}/drafts`, { recipe: draft }, authHeaders());
            setRecipe(saved.data.recipe);
            setAccount((a) => ({ ...a, username: a.username || frame?.variables?.username || '' }));
            await axios.delete(`${RECIPES}/studio/sessions/${session.id}`, authHeaders()).catch(() => {});
            sessionRef.current = null;
            setSession(null);
            setStep(5);
        } catch (e) {
            fail(e, t('dataSources.web.finishError', 'Le parcours n’a pas pu être enregistré.'));
        } finally {
            setBusy(null);
        }
    };

    const runTest = async () => {
        if (!recipe) return;
        setBusy('test');
        setMessage(null);
        setTestResult(null);
        try {
            const variables = { ...identifiers, username: account.username, password: account.password };
            const res = await axios.post(`${RECIPES}/${encodeURIComponent(recipe.db_id || recipe.id)}/test-run`, { variables }, authHeaders());
            setTestResult(res.data);
            if (res.data.recipe) setRecipe(res.data.recipe);
        } catch (e) {
            fail(e, t('dataSources.readError', 'La lecture a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const buildSpec = () => {
        const extracted = testResult?.run?.extracted || recipe?.extracted_sample || {};
        const mapping = {};
        Object.keys(extracted).forEach((name) => {
            if (LIST_SECTIONS.includes(name)) {
                const first = Array.isArray(extracted[name]) && extracted[name][0] ? extracted[name][0] : {};
                mapping[name] = { each: `$.${name}`, fields: Object.fromEntries(Object.keys(first).map((f) => [f, `$.${f}`])) };
                return;
            }
            const meta = FIELD_SECTIONS.find((s) => !s.list && name.startsWith(`${s.section}_`));
            if (!meta) return;
            const fieldName = name.slice(meta.section.length + 1);
            mapping[meta.section] = { ...(mapping[meta.section] || {}), [fieldName]: `$.${name}` };
        });
        const variables = {};
        (recipe?.variables || []).forEach((v) => { if (SEARCH_VARIABLES.includes(v)) variables[v] = `{${v}}`; });
        const inputs = Object.keys(variables);
        const spec = {
            spec_version: 1, title, transport: 'browser_recipe', inputs, require_any: inputs.filter((i) => i !== 'client_name'), params: {},
            steps: [], recipe: { id: recipe.id, variables, not_found_when: { empty: `$.${Object.keys(mapping.order || {}).length ? `order_${Object.keys(mapping.order)[0]}` : Object.keys(extracted)[0] || 'order_status'}` } },
            mapping, modes, operations: { timeout_s: 180, retries: 0, health_alerts: true }, presentation: { path: 'web', draft_id: draftId.current, recipe_id: recipe.id },
            ...(category ? { category } : {}),
        };
        if (modes.includes('ai_tool')) spec.tool = { name: `lookup_${key}`, description: `Look up an order in ${title}` };
        if (modes.includes('scheduled_pull')) spec.schedule = { every_minutes: 360 };
        if (effitrace && verifyDays) { spec.shadow_of = 'effitrace'; spec.verification = { days: 14 }; }
        return spec;
    };

    const activate = async () => {
        if (!recipe || !key) return;
        setBusy('save');
        setMessage(null);
        try {
            await axios.post(`${RECIPES}/${encodeURIComponent(recipe.db_id || recipe.id)}/status`, { status: 'published' }, authHeaders());
            const spec = buildSpec();
            await axios.put(`${API}/${encodeURIComponent(key)}`, { spec, base_url: startUrl, auth: { type: 'portal', username: account.username, password: account.password }, enabled: true }, authHeaders());
            onDone(key);
        } catch (e) {
            fail(e, t('dataSources.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const elements = (frame?.elements || []).filter((e) => !filter || JSON.stringify(e).toLowerCase().includes(filter.toLowerCase()));
    const canPublish = recipe && account.username && account.password && ['success', 'stopped_before_submit'].includes(testResult?.run?.status || recipe?.last_test?.status);
    const stepLabel = (name) => t(`dataSources.webSteps.${name}`, WEB_LABELS[name] || name);
    const extracted = frame?.extracted && Object.keys(frame.extracted).length > 0 ? frame.extracted : null;

    const recorder = (
        <div className="ds-recorder">
            <div className="ds-recorder__frame">
                <div className="ds-recorder__url ds-mono" title={frame?.url || ''}>{frame?.url}</div>
                {frameSrc
                    ? <img src={frameSrc} alt={t('dataSources.web.frameAlt', 'Page du portail')} />
                    : <div className="ds-recorder__empty">{t('dataSources.web.noFrame', 'Aucune image de la page pour le moment.')}</div>}
                <div className="ds-recorder__foot">
                    <span className="u-muted">{t('dataSources.web.recorded', '{{n}} actions enregistrées', { n: frame?.steps ?? 0 })}</span>
                    <button type="button" className="u-btn u-btn--ghost u-btn--sm" disabled={busy === 'act'} onClick={undo}>
                        <Undo2 aria-hidden="true" /> {t('dataSources.web.undo', 'Annuler la dernière')}
                    </button>
                </div>
            </div>
            <div className="ds-recorder__side">
                <input className="u-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('dataSources.web.filter', 'Filtrer les éléments de la page')}
                    aria-label={t('dataSources.web.filter', 'Filtrer les éléments de la page')} />
                <div className="ds-elements">
                    {elements.map((e) => (
                        <button type="button" key={e.k} className={`ds-element${selected?.k === e.k ? ' is-selected' : ''}`} aria-pressed={selected?.k === e.k}
                            onClick={() => { setSelected(e); setTablePick(null); }}>
                            <span className="u-tag ds-element__role">{e.role || e.tag}</span>
                            <span className="ds-element__label">{e.label || e.placeholder || e.text || e.id || e.css}</span>
                        </button>
                    ))}
                    {!elements.length && <p className="u-help ds-elements__empty">{t('dataToolsUi.web.noElements', 'Aucun élément ne correspond.')}</p>}
                </div>
                {selected && (
                    <div className="ds-element-actions">
                        <div className="ds-element-actions__title"><MousePointerClick aria-hidden="true" /> {selected.label || selected.text || selected.css}</div>
                        {step !== 4 && (
                            <>
                                {['textbox', 'combobox', null].includes(selected.role) && selected.tag !== 'select' && (
                                    <div className="ds-toolbar">
                                        <input className="u-input ds-grow" value={value} onChange={(e) => setValue(e.target.value)} placeholder={t('dataSources.web.typedValue', 'Valeur à saisir')}
                                            aria-label={t('dataSources.web.typedValue', 'Valeur à saisir')} type={secret ? 'password' : 'text'} />
                                        {step === 2 ? (
                                            <select className="u-select ds-short" value={variable} aria-label={t('dataToolsUi.web.valueIs', 'Cette valeur est')}
                                                onChange={(e) => { setVariable(e.target.value); setSecret(e.target.value === 'password'); }}>
                                                <option value="username">{t('dataSources.web.varUsername', 'identifiant du compte')}</option>
                                                <option value="password">{t('dataSources.web.varPassword', 'mot de passe (non conservé ; redemandé à la fin)')}</option>
                                                <option value="">{t('dataSources.web.varNone', 'valeur fixe')}</option>
                                            </select>
                                        ) : (
                                            <select className="u-select ds-short" value={clientBound ? 'client_name' : variable} aria-label={t('dataToolsUi.web.valueIs', 'Cette valeur est')}
                                                onChange={(e) => { setClientBound(e.target.value === 'client_name'); setVariable(e.target.value); }}>
                                                {SEARCH_VARIABLES.map((v) => <option key={v} value={v}>{t(`ticketWorkflow.identifier.${v}`, v)}</option>)}
                                                <option value="">{t('dataSources.web.varNone', 'valeur fixe')}</option>
                                            </select>
                                        )}
                                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={busy === 'act'} onClick={fillVariable}>{t('dataSources.web.fill', 'Saisir')}</button>
                                    </div>
                                )}
                                {(selected.tag === 'select' || selected.role === 'combobox') && (
                                    <div className="ds-toolbar">
                                        <select className="u-select ds-grow" value={value} onChange={(e) => setValue(e.target.value)} aria-label={t('dataSources.web.chooseOption', 'Choisir une option…')}>
                                            <option value="">{t('dataSources.web.chooseOption', 'Choisir une option…')}</option>
                                            {(selected.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                        {step === 3 && (
                                            <Switch id="ds-web-client-bound" checked={clientBound} onChange={setClientBound} label={t('dataSources.web.dependsOnClient', 'change selon le client')} />
                                        )}
                                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={busy === 'act' || !value} onClick={selectVariable}>{t('dataSources.web.select', 'Choisir')}</button>
                                    </div>
                                )}
                                <div>
                                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={busy === 'act'} onClick={() => act('click')}>{t('dataSources.web.click', 'Cliquer')}</button>
                                </div>
                            </>
                        )}
                        {step === 4 && (
                            <div className="ds-toolbar">
                                <select className="u-select ds-grow" value={`${field.section}.${field.name}`} aria-label={t('dataSources.web.whatIsIt', 'C’est quoi ?')}
                                    onChange={(e) => { const [section, name] = e.target.value.split('.'); setField({ section, name }); }}>
                                    {FIELD_SECTIONS.filter((s) => !s.list).map((s) => s.fields.map((f) => (
                                        <option key={`${s.section}.${f}`} value={`${s.section}.${f}`}>{t(`connectorStudio.section_${s.section}`, s.section)} · {t(`dataSources.field.${s.section}.${f}`, f)}</option>
                                    )))}
                                </select>
                                <button type="button" className="u-btn u-btn--primary u-btn--sm" disabled={busy === 'act'} onClick={extractField}>{t('dataSources.web.read', 'Lire cette valeur')}</button>
                                <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={busy === 'act'} onClick={() => act('click')}>{t('dataSources.web.click', 'Cliquer')}</button>
                            </div>
                        )}
                    </div>
                )}
                {step === 4 && (frame?.tables || []).length > 0 && (
                    <div className="ds-tables">
                        <span className="u-overline">{t('dataSources.web.tables', 'Tableaux de la page')}</span>
                        {frame.tables.map((tb) => (
                            <button type="button" key={tb.t} className={`ds-element${tablePick?.t === tb.t ? ' is-selected' : ''}`}
                                onClick={() => { setSelected(null); setTablePick({ t: tb.t, section: 'shipments', columns: {} }); }}>
                                <Table className="ds-element__icon" aria-hidden="true" />
                                <span className="ds-element__label">{(tb.headers || []).filter(Boolean).slice(0, 5).join(' · ') || tb.css} ({tb.rows})</span>
                            </button>
                        ))}
                        {tablePick && (() => {
                            const tb = frame.tables.find((x) => x.t === tablePick.t);
                            return tb && (
                                <div className="ds-element-actions">
                                    <select className="u-select" value={tablePick.section} aria-label={t('dataToolsUi.web.tableIs', 'Ce tableau contient')}
                                        onChange={(e) => setTablePick({ ...tablePick, section: e.target.value })}>
                                        {LIST_SECTIONS.map((s) => <option key={s} value={s}>{t(`connectorStudio.section_${s}`, s)}</option>)}
                                    </select>
                                    {(tb.headers || []).map((h, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <div key={i} className="ds-column-map">
                                            <span className="ds-column-map__name" title={h || `#${i + 1}`}>{h || `#${i + 1}`}{tb.sample?.[i] ? ` — ${tb.sample[i]}` : ''}</span>
                                            <select className="u-select" value={tablePick.columns[i] || ''} aria-label={h || `#${i + 1}`}
                                                onChange={(e) => setTablePick({ ...tablePick, columns: { ...tablePick.columns, [i]: e.target.value } })}>
                                                <option value="">{t('dataSources.web.ignoreColumn', '(ignorer)')}</option>
                                                {FIELD_SECTIONS.find((s) => s.section === tablePick.section).fields.map((f) => <option key={f} value={f}>{t(`dataSources.field.${tablePick.section}.${f}`, f)}</option>)}
                                            </select>
                                        </div>
                                    ))}
                                    <div>
                                        <button type="button" className="u-btn u-btn--primary u-btn--sm" disabled={busy === 'act' || !Object.values(tablePick.columns).some(Boolean)} onClick={extractTable}>
                                            {t('dataSources.web.readTable', 'Lire ce tableau')}
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
                {step === 4 && (
                    <div className="ds-hints">
                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={busy === 'hints' || !frame?.text} onClick={readPageHints}>
                            <Sparkles aria-hidden="true" /> {t('dataSources.web.suggest', 'Que voit l’assistant sur cette page ?')}
                        </button>
                        {hints && Object.keys(hints.fields || {}).length > 0 && (
                            <DescriptionList items={Object.entries(hints.fields).map(([k, v]) => ({ label: k, value: String(v) }))} />
                        )}
                        {hints && Object.keys(hints.fields || {}).length === 0 && <p className="u-help">{t('dataSources.web.noHints', 'Aucune valeur reconnue sur cette page.')}</p>}
                    </div>
                )}
                {extracted && (
                    <div className="ds-extracted">
                        <span className="u-overline">{t('dataSources.web.readSoFar', 'Lu jusqu’ici')}</span>
                        <DescriptionList items={Object.entries(extracted).map(([k, v]) => ({
                            label: k, value: Array.isArray(v) ? `${formatCount(v.length)} ${t('dataSources.step4.items', 'éléments')}` : String(v),
                        }))} />
                    </div>
                )}
            </div>
        </div>
    );

    const stepBody = () => {
        if (step === 1) {
            return (
                <div className="u-form">
                    <Field label={t('dataSources.step1.name', 'Nom de la source')} htmlFor="ds-web-name">
                        <input id="ds-web-name" className="u-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('dataSources.web.namePlaceholder', 'ex. Extranet WMS Acme')} />
                    </Field>
                    <CategorySelect t={t} id="ds-web-category" value={category} onChange={setCategory} />
                    <Field label={t('dataSources.web.startUrl', 'Adresse de la page de connexion du portail')} htmlFor="ds-web-url"
                        help={t('dataSources.web.intro', 'Le navigateur tourne sur le serveur, avec l’adresse IP que le portail connaît. Vous montrez une fois comment vous vous connectez, cherchez une commande et lisez ses informations ; rien n’est modifié sur le portail.')}>
                        <input id="ds-web-url" className="u-input ds-mono" value={startUrl} onChange={(e) => setStartUrl(e.target.value)} placeholder="https://extranet.mon-wms.com/login" />
                    </Field>
                </div>
            );
        }
        if (step === 2) {
            return (
                <div className="u-form">
                    <p className="u-help ds-help">{t('dataSources.web.step2Hint', 'Choisissez le champ identifiant dans la liste, saisissez-le ; puis le mot de passe (il reste dans le navigateur du serveur, jamais dans la source) ; puis cliquez le bouton de connexion.')}</p>
                    {!session ? (
                        <EmptyState icon={Globe} title={t('dataToolsUi.web.openTitle', 'Ouvrir le portail sur le serveur')}
                            action={(
                                <button type="button" className="u-btn u-btn--primary" disabled={busy === 'open' || !startUrl} onClick={openSession}>
                                    <Globe aria-hidden="true" /> {busy === 'open' ? t('dataSources.web.opening', 'Ouverture…') : t('dataSources.web.open', 'Ouvrir le portail')}
                                </button>
                            )}>
                            <span className="ds-mono">{startUrl}</span>
                        </EmptyState>
                    ) : recorder}
                </div>
            );
        }
        if (step === 3 && session) {
            return (
                <div className="u-form">
                    <p className="u-help ds-help">{t('dataSources.web.step3Hint', 'Cherchez une commande comme vous le faites d’habitude. Pour chaque valeur saisie, dites de quel identifiant il s’agit ; une liste qui change selon le client est liée à la fiche client.')}</p>
                    {recorder}
                </div>
            );
        }
        if (step === 4 && session) {
            return (
                <div className="u-form">
                    <p className="u-help ds-help">{t('dataSources.web.step4Hint', 'Sur la page de la commande, sélectionnez une information dans la liste et dites ce que c’est. Un tableau (colis, événements, lignes) se lit en une fois en nommant ses colonnes.')}</p>
                    {recorder}
                </div>
            );
        }
        if (step === 5) {
            const run = testResult?.run;
            return (
                <div className="u-form">
                    {recipe && (
                        <Banner tone="info">
                            {t('dataSources.web.recipeSaved', 'Parcours enregistré ({{n}} actions, {{v}} variables). Vérifiez-le avec une vraie commande, puis activez la source.', { n: (recipe.steps || []).length, v: (recipe.variables || []).length })}
                        </Banner>
                    )}
                    <div className="u-field">
                        <span className="u-label">{t('dataSources.web.account', 'Compte utilisé par le système')}</span>
                        <p className="u-help">{t('dataSources.web.accountHint', 'Le mot de passe saisi pendant l’enregistrement n’a pas été conservé. Celui-ci est chiffré et sert uniquement aux lectures.')}</p>
                        <div className="u-form-grid">
                            <input className="u-input" autoComplete="off" value={account.username} placeholder={t('dataSources.web.varUsername', 'identifiant du compte')}
                                aria-label={t('dataSources.web.varUsername', 'identifiant du compte')} onChange={(e) => setAccount({ ...account, username: e.target.value })} />
                            <input className="u-input" type="password" autoComplete="new-password" value={account.password} placeholder="••••••••"
                                aria-label={t('dataSources.authField.password', 'Mot de passe')} onChange={(e) => setAccount({ ...account, password: e.target.value })} />
                        </div>
                    </div>
                    <div className="u-field">
                        <span className="u-label">{t('dataSources.step5.check', 'Vérifier avec une commande')}</span>
                        <div className="ds-toolbar">
                            <IdentifierInputs t={t} names={(recipe?.variables || []).filter((v) => SEARCH_VARIABLES.includes(v))} values={identifiers} onChange={setIdentifiers} idPrefix="ds-web-verify" />
                            <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'test' || !recipe || !account.username || !account.password} onClick={runTest}>
                                <Play aria-hidden="true" /> {t('dataSources.step5.verify', 'Vérifier avec cette commande')}
                            </button>
                        </div>
                    </div>
                    {run && (
                        <Banner tone={run.status === 'success' ? 'success' : 'danger'}>
                            {run.status === 'success'
                                ? t('dataSources.web.testOk', 'Lecture réussie en {{ms}} ms : {{n}} valeurs.', { ms: run.duration_ms, n: Object.keys(run.extracted || {}).length })
                                : `${run.failure_category || run.status}${run.error ? ` — ${run.error}` : ''} (${run.reached_step})`}
                        </Banner>
                    )}
                    {run?.extracted && <pre className="ds-pre">{JSON.stringify(run.extracted, null, 2)}</pre>}
                    <div className="u-field">
                        <span className="u-label">{t('dataSources.step5.usage', 'Quand l’utiliser')}</span>
                        <UsageSwitches t={t} modes={modes} onToggle={(mode, on) => setModes(on ? [...modes, mode] : modes.filter((m) => m !== mode))} idPrefix="ds-web-usage" />
                        {effitrace && (
                            <Switch id="ds-web-verify-days" checked={verifyDays} onChange={setVerifyDays}
                                label={`${t('dataSources.catalog.verify', 'Comparer pendant 14 jours avant de remplacer')} — ${effitrace.verification_hint}`} />
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <Card
            className="ds-wizard"
            title={`${step}. ${stepLabel(WEB_STEPS[step - 1])}`}
            description={t('dataToolsUi.web.desc', 'Le navigateur du serveur ouvre le portail ; vous montrez une fois le chemin, il le refait à chaque ticket.')}
            footerNote={t('dataToolsUi.create.stepOf', 'Étape {{step}} sur {{total}}', { step, total: WEB_STEPS.length })}
            footer={(
                <div className="ds-wizard__nav">
                    {step === 1 && <button type="button" className="u-btn u-btn--secondary" onClick={onBack}><ArrowLeft aria-hidden="true" /> {t('dataSources.back', 'Retour')}</button>}
                    {step > 1 && step < 5 && (
                        <button type="button" className="u-btn u-btn--secondary" onClick={() => setStep(step - 1)}>
                            <ArrowLeft aria-hidden="true" /> {t('dataSources.prev', 'Précédent')}
                        </button>
                    )}
                    {step === 1 && (
                        <button type="button" className="u-btn u-btn--primary" disabled={!title || !/^https?:\/\//.test(startUrl)} onClick={() => setStep(2)}>
                            {t('dataSources.next', 'Suivant')} <ArrowRight aria-hidden="true" />
                        </button>
                    )}
                    {(step === 2 || step === 3) && (
                        <button type="button" className="u-btn u-btn--primary" disabled={!session} onClick={() => setStep(step + 1)}>
                            {t('dataSources.next', 'Suivant')} <ArrowRight aria-hidden="true" />
                        </button>
                    )}
                    {step === 4 && (
                        <button type="button" className="u-btn u-btn--primary" disabled={busy === 'finish' || !extracted} onClick={finish}>
                            {t('dataSources.web.finish', 'Terminer le parcours')} <ArrowRight aria-hidden="true" />
                        </button>
                    )}
                    {step === 5 && (
                        <button type="button" className="u-btn u-btn--primary" disabled={busy === 'save' || !canPublish} onClick={activate}>
                            <Save aria-hidden="true" /> {t('dataSources.activate', 'Enregistrer et activer')}
                        </button>
                    )}
                </div>
            )}
        >
            <Stepper names={WEB_STEPS} labelOf={stepLabel} step={step} label={t('dataToolsUi.create.steps', 'Étapes')} />
            <div className="ds-wizard__body">
                <Message message={message} />
                {stepBody()}
            </div>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Path C — catalogue
// ---------------------------------------------------------------------------

export const CatalogInstall = ({ t, entries, onBack, onDone }) => {
    const [entry, setEntry] = useState(null);
    const [fields, setFields] = useState({});
    const [verify, setVerify] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const choose = (e) => { setEntry(e); setFields(Object.fromEntries((e.fields || []).map((f) => [f.name, f.default || '']))); setError(null); };
    const install = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await axios.post(`${API}/catalog/${entry.system}/install`, { fields, brand_id: entry.brand_id || null, verify_days: entry.verification_available && verify ? 14 : null }, authHeaders());
            onDone(res.data.key);
        } catch (e) {
            setError(failText(e, t('dataSources.installError', 'L’installation a échoué.')));
        } finally {
            setBusy(false);
        }
    };

    if (!entry) {
        return (
            <Card title={t('dataSources.catalog.title', 'Systèmes connus')}
                description={t('dataToolsUi.catalog.desc', 'Les systèmes qu’Unanimity sait déjà lire : une seule étape, avec vos accès.')}
                footer={<button type="button" className="u-btn u-btn--secondary" onClick={onBack}><ArrowLeft aria-hidden="true" /> {t('dataSources.back', 'Retour')}</button>}
                flush>
                {entries.length === 0 ? (
                    <EmptyState icon={ListChecks} title={t('dataToolsUi.catalog.empty', 'Aucun système dans la liste')} />
                ) : (
                    <ul className="u-list">
                        {entries.map((e) => (
                            <li key={`${e.system}:${e.brand_id || ''}`} className="u-row">
                                <IconChip icon={e.kind === 'integration' ? Plug : ListChecks} tone="neutral" />
                                <div className="u-row__main">
                                    <div className="u-row__title">
                                        {e.title}
                                        {e.installed && <Badge tone="success" dot>{t('dataToolsUi.catalog.installed', 'Installé')}</Badge>}
                                    </div>
                                    <div className="u-row__meta u-row__meta--wrap">
                                        {e.description}
                                        {e.installed && ` ${t('dataSources.catalog.installed', 'déjà installé — réinstaller met à jour')}`}
                                    </div>
                                </div>
                                <div className="u-row__side">
                                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => choose(e)}>
                                        {e.installed ? t('dataToolsUi.catalog.update', 'Mettre à jour') : t('dataSources.catalog.install', 'Installer')}
                                        <ChevronRight aria-hidden="true" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        );
    }

    return (
        <Card
            icon={ListChecks}
            title={entry.title}
            description={entry.description}
            footer={(
                <>
                    <button type="button" className="u-btn u-btn--secondary" onClick={() => setEntry(null)} disabled={busy}>
                        <ArrowLeft aria-hidden="true" /> {t('dataSources.back', 'Retour')}
                    </button>
                    <button type="button" className="u-btn u-btn--primary" disabled={busy || entry.fields.some((f) => f.required && !fields[f.name])} onClick={install}>
                        <Save aria-hidden="true" /> {t('dataSources.catalog.install', 'Installer')}
                    </button>
                </>
            )}
        >
            <div className="u-form">
                {error && <Banner tone="danger">{error}</Banner>}
                {entry.fields.length === 0 && <p className="u-help">{t('dataToolsUi.catalog.noFields', 'Aucun accès à saisir : l’installation est immédiate.')}</p>}
                {entry.fields.length > 0 && (
                    <div className="u-form-grid">
                        {entry.fields.map((f) => (
                            <Field key={f.name} label={`${f.label}${f.required ? ' *' : ''}`} htmlFor={`ds-cat-${f.name}`}
                                help={f.type === 'secret' ? t('dataToolsUi.secret.help', 'Enregistré pour les lectures, jamais réaffiché.') : undefined}>
                                <input id={`ds-cat-${f.name}`} className="u-input" type={f.type === 'secret' ? 'password' : 'text'} autoComplete={f.type === 'secret' ? 'new-password' : 'off'}
                                    value={fields[f.name] || ''} placeholder={f.type === 'secret' ? '••••••••' : ''} onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })} />
                            </Field>
                        ))}
                    </div>
                )}
                {entry.verification_available && (
                    <Switch id="ds-cat-verify" checked={verify} onChange={setVerify}
                        label={`${t('dataSources.catalog.verify', 'Comparer pendant 14 jours avant de remplacer')} — ${entry.verification_hint}`} />
                )}
            </div>
        </Card>
    );
};

